/**
 * Insurance (Ladder) Backend API Types
 * Mirrors the mobile app's `insurance` service contract (ladder.wavvest.com).
 */

// How recently the applicant used tobacco. Values are sent to the API as-is.
export enum TobaccoUse {
  Never = "never",
  WithinLast12Months = "within_last_12_months",
  TwelveToTwentyThreeMonths = "12_to_23_months",
  TwentyFourToThirtyFiveMonths = "24_to_35_months",
  ThirtySixMonthsOrGreater = "36_months_or_greater",
}

// Applicant sex. Enum values are the lowercase strings the API expects.
export enum GenderOption {
  Male = "male",
  Female = "female",
  Other = "other",
}

// Portal branching state for the insurance pillar.
export type InsuranceStatus =
  | "not_started"
  | "authorized_has_policy"
  | "authorized_no_policy"
  | "not_authorized";

// Core quote request. Required fields drive the quote; the rest are optional
// enrichment (mostly prefilled from the application/identity later).
export interface QuoterBody {
  height_inches: number;
  household_income: number;
  date_of_birth: string; // YYYY-MM-DD
  tobacco_use: TobaccoUse;
  zipcode: string;
  sex: GenderOption;
  weight_pounds: number;
  family_history: boolean;
  coverage?: number;
  term?: number;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  street_address?: string;
  city?: string;
  state?: string;
  user_age?: number;
  income?: number;
  dl_number?: string;
  dl_state?: string;
  lddr_creative?: string;
  family_history_members?: { relation: string; condition: string }[];
  mortgage?: number;
  child_count?: number;
  citizenship?: string;
  employment?: string;
  external_id?: string;
}

// Coverage-needs calculator (calculate_coverage). Not used by the core quote
// flow (which defaults coverage to $1M), but the response's `nearest_policy`
// is a good default for the coverage selector.
export interface CoverageBody {
  mortgage_balance: number | null;
  other_debts: number | null;
  children_education_tuition: number | null;
  desired_yearly_cash: number | null;
  desired_payout_years: number | null;
  additional_coverage: number | null;
  existing_coverage: number | null;
  current_savings: number | null;
  children: { age: number }[];
}

export interface CoverageResponse {
  debt: number;
  current_resources: number;
  living_expenses: number;
  tuition_costs: number;
  additional_coverage: number;
  total_insurance_needs: number;
  nearest_policy: number;
}

export interface Quote {
  coverage: number;
  monthly_premium: number;
  term: number;
  daily_price: number;
}

export interface QuoteStatus {
  valid: boolean;
  complete: boolean;
  eligible: boolean;
}

export interface QuoterResponse {
  id: string;
  quote_status: QuoteStatus;
  quotes: Quote[];
}

// The application request is the same shape as the quote request.
export type ApplicationBody = QuoterBody;

// start_application returns a Ladder-hosted application URL (valid until
// url_expires, a unix timestamp).
export interface ApplicationCreationResponse {
  id: string;
  url: string;
  url_expires: number;
}

export interface ApplicationResponse {
  status: string;
  suggested_messaging: {
    title: string;
    subtitle: string;
    call_to_action: string;
    alternative_call_to_action: string;
  };
  ineligibility?: { is_eligible: boolean };
  quote_questions_answered?: string[];
  offer_details?: {
    face_amount: number;
    monthly_premium: number;
    term: number;
    expires_unix: number;
  };
  policy_details?: {
    id: string;
    face_amount: number;
    monthly_premium: number;
    term: number;
    issue_timestamp: number;
  };
}

export interface PolicyInfo {
  policy_id: string;
  active_coverage: number;
  active_term: number;
  active_monthly_premium: number;
  ladder_down_prices: { new_coverage: number; new_monthly_premium: number }[];
}

export interface PolicyResponse {
  active_policies: PolicyInfo[];
}
